import { Injectable, Output, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoadingService } from './loading.service';
import { AuthService } from './auth.service';
import { environment as env } from '../../environments/environment';
import { ToastService } from './toast.service';
const localLog = console.log;

@Injectable({
  providedIn: 'root'
})
export class UsersService {

  constructor(private http: HttpClient,
    private loadingService: LoadingService,
    private authService: AuthService,
    private toast: ToastService) {

  }

  private _get_headers() {
    return {      withCredentials: true
    };
  }

  async getRoles(): Promise<any> {
    let _id = this.loadingService.add('Loading Roles');
    try {
      let res: any = await this.http.get(
        `${env.apiUrl}/rest/admin/roles`,
        this._get_headers(),
      ).toPromise();

      if (res?.err) {
        throw new Error(res.err);
      }

      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error getRoles() =>', e);
      this.toast.error('Failed to get roles', e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  async getCustomers(): Promise<any> {
    let _id = this.loadingService.add('Loading Customers');
    try {
      let res: any = await this.http.get(
        `${env.apiUrl}/rest/admin/customers`,
        this._get_headers(),
      ).toPromise();

      if (res?.err) {
        throw new Error(res.err);
      }

      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error getCustomers() =>', e);
      this.toast.error('Failed to get customers', e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  async getUsers(): Promise<any> {
    let _id = this.loadingService.add('Loading Users');
    try {
      let res: any = await this.http.get(
        `${env.apiUrl}/rest/admin/users`,
        this._get_headers(),
      ).toPromise();

      if (res?.err) {
        throw new Error(res.err);
      }

      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error getUsers() =>', e);
      this.toast.error('Failed to get users', e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  // IT-F2-426 Mike 2026-10-05 cid:a47ab60d: narrowed roster for the
  // Add-from-PDF user_id typeahead. Backend GET /admin/users already
  // accepts ?customer=<slug> which filters to that customer's pool
  // after the JWT scope intersection. Silent on error — the typeahead
  // falls back to free-text entry so the admin can still save.
  async getUsersForCustomer(customer_slug: string): Promise<any> {
    try {
      const url = `${env.apiUrl}/rest/admin/users?customer=${encodeURIComponent(customer_slug)}`;
      const res: any = await this.http.get(url, this._get_headers()).toPromise();
      return res;
    } catch (e: any) {
      console.log('getUsersForCustomer() =>', e);
      return { err: e?.message || String(e) };
    }
  }

  async getUserActivity(username: any): Promise<any> {
    // encodeURIComponent so emails with @ / . survive path segmentation;
    // the backend @Get('/users/:username') accepts a single segment.
    const url = `${env.apiUrl}/rest/admin/users/${encodeURIComponent(username)}`;
    let _id = this.loadingService.add('Loading user activity...')
    try {
      let res: any = await this.http.get(url, this._get_headers()).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error getUserActivity() =>', e);
      const status = e?.status ?? e?.error?.status;
      const responseText = typeof e?.error === 'string'
        ? e.error.slice(0, 300)
        : (e?.error?.text || e?.error?.message || e?.message || '');
      // Funnel into /admin/error-reports with a support ID so cross-pool
      // lookup misses are diagnosable — matches the billing pattern
      // (feedback_silent_bounce_needs_jwt_peek + Mike 2026-07-06). Any
      // 'User does not exist' from get_user_details_across_pools now
      // lands with the email + URL + status captured server-side.
      this.toast.error('Failed to get user activity', e?.message || String(e), {
        error_code: 'F2ADMIN_USER_ACTIVITY_E01',
        customer: 'f2-admin',
        scanner_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
        url,
        http_status: typeof status === 'number' ? status : undefined,
        f2_response_body: String(responseText).slice(0, 500),
        f2_response_message: e?.message || String(e),
        js_error_name: e?.name || undefined,
        email_attempted: typeof username === 'string' ? username : undefined,
      });
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  async createUser(data: any): Promise<any> {
    let _id = this.loadingService.add('Creating User');
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/admin/users`,
        data,
        this._get_headers(),
      ).toPromise();

      if (res?.err) {
        // Backend's out-of-scope reject is almost always cross-tab
        // cookie pollution (a login on members.f2-tech.ai or any
        // sibling *.f2-tech.ai overwrites this tab's admin cookies).
        // Re-fetch /rest/api/session and compare sub — if it changed
        // from what we cached at bootstrap, surface a specific alert
        // instead of the generic "failed to create user" toast.
        // Mike / ER-ASWJRE9 2026-07-02.
        try {
          if (/customer in your scope/i.test(String(res.err))) {
            await this._detect_cross_tab_session_swap(data?.email);
          }
        } catch (_) { /* diagnostics only — never mask the real error */ }
        throw new Error(res.err);
      }

      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error createUser() =>', e);
      this.toast.error('Failed to create user: ' + data.email, e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  /**
   * If backend rejected with "at least one customer in your scope is
   * required.", the caller identity the backend saw probably isn't the
   * one this tab thinks it's signed in as. Confirm by asking
   * /rest/api/session for the CURRENT cookie identity and diffing
   * against the AuthService cache. If different, raise a distinct
   * "session swapped" toast that tells the operator exactly what to
   * do (sign back into admin), rather than the generic error message.
   */
  private async _detect_cross_tab_session_swap(_target_email: string | undefined) {
    const cached = this.authService.get_user();
    const cachedSub = String(cached?.sub || '').trim();
    const cachedEmail = String(cached?.email || cached?.username || '').trim();
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/api/session`, this._get_headers())
        .toPromise();
      const liveSub = String(res?.user?.sub || '').trim();
      const liveEmail = String(res?.user?.email || res?.user?.username || '').trim();
      if (cachedSub && liveSub && liveSub !== cachedSub) {
        this.toast.error(
          'Session changed in another tab',
          `This tab thought you were ${cachedEmail || '(unknown)'}, but the browser cookies now identify ` +
          `${liveEmail || '(unknown)'}. A login on another *.f2-tech.ai tab overwrote the admin session ` +
          `cookies. Sign back into admin.f2-tech.ai (or close the other tab) and retry.`,
        );
      }
    } catch (_) { /* diagnostics only */ }
  }

  /**
   * Returns the set of pool_ids that already hold a user identity with
   * the given email. Powers the Add-User picker's "already exists in
   * this pool → disable the corresponding customer checkbox" UX.
   * Empty array on error so the picker doesn't lock up if the backend
   * is unreachable. Mike 2026-06-26.
   */
  async lookupPoolsForEmail(email: string): Promise<string[]> {
    if (!email) return [];
    try {
      const res: any = await this.http.get(
        `${env.apiUrl}/rest/admin/users/lookup-pools-for-email`,
        { ...this._get_headers(), params: { email } },
      ).toPromise();
      return Array.isArray(res?.pool_ids) ? res.pool_ids : [];
    } catch (e: any) {
      console.warn('lookupPoolsForEmail() =>', e?.message || e);
      return [];
    }
  }

  /**
   * Update user attributes in a specific Cognito pool. poolId MUST be
   * the row's `pool_id` (stamped by get_users_across_pools) for
   * per-customer-pool users; same username/email exists in multiple
   * pools and the backend would otherwise default to legacy and
   * throw UserNotFoundException ("User does not exist").
   */
  async updateUserAttributes(username: string, data: any, poolId?: string): Promise<any> {
    let _id = this.loadingService.add('Updating user attributes');
    try {
      const body = poolId ? { ...data, pool_id: poolId } : data;
      let res: any = await this.http.put(
        `${env.apiUrl}/rest/admin/users/${username}`,
        body,
        this._get_headers(),
      ).toPromise();

      if (res?.err) {
        throw new Error(res.err);
      }

      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error updateUserAttributes() =>', e);
      this.toast.error('Failed to update user attributes', e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  /**
   * Delete a user from a specific Cognito pool. poolId MUST be the row's
   * `pool_id` (stamped by get_users_across_pools) when the same email
   * exists in multiple pools — without it the backend defaults to legacy
   * and silently deletes the wrong identity (or 404s if the username
   * doesn't exist in legacy).
   */
  async deleteUser(username: string, poolId?: string): Promise<any> {
    let _id = this.loadingService.add('Deleting user');
    try {
      const url = poolId
        ? `${env.apiUrl}/rest/admin/users/${username}?pool_id=${encodeURIComponent(poolId)}`
        : `${env.apiUrl}/rest/admin/users/${username}`;
      let res: any = await this.http.delete(url, this._get_headers()).toPromise();

      if (res?.err) {
        throw new Error(res.err);
      }

      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error deleteUser() =>', e);
      this.toast.error('Failed to delete user', e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  /**
   * Pending-deletion queue API — client_admin requests deletion, super-
   * admin approves/rejects via the admin "Pending Deletions" page.
   * Backend route auth: request-deletion accepts admin OR client_admin;
   * get/approve/reject are super-admin only.
   */
  async requestUserDeletion(username: string, reason?: string): Promise<any> {
    let _id = this.loadingService.add('Requesting user deletion');
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/admin/users/${username}/request-deletion`,
        { reason: reason || '' },
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error requestUserDeletion() =>', e);
      this.toast.error('Failed to request user deletion', e.message);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  async getPendingDeletions(): Promise<any> {
    let _id = this.loadingService.add('Loading Pending Deletions');
    try {
      let res: any = await this.http.get(
        `${env.apiUrl}/rest/admin/pending-deletions`,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error getPendingDeletions() =>', e);
      this.toast.error('Failed to load pending deletions', e.message);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  async approveUserDeletion(username: string): Promise<any> {
    let _id = this.loadingService.add('Approving deletion');
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/admin/users/${username}/approve-deletion`,
        {},
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error approveUserDeletion() =>', e);
      this.toast.error('Failed to approve deletion', e.message);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  async rejectUserDeletion(username: string, reason?: string): Promise<any> {
    let _id = this.loadingService.add('Rejecting deletion');
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/admin/users/${username}/reject-deletion`,
        { reason: reason || '' },
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error rejectUserDeletion() =>', e);
      this.toast.error('Failed to reject deletion', e.message);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  async enableUser(username: string, enabled: boolean, poolId?: string): Promise<any> {
    let _id = this.loadingService.add('Updating user access');
    try {
      const body: any = { enabled };
      if (poolId) body.pool_id = poolId;
      let res: any = await this.http.put(
        `${env.apiUrl}/rest/admin/users/${username}/enable`,
        body,
        this._get_headers(),
      ).toPromise();

      if (res?.err) {
        throw new Error(res.err);
      }

      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error enableUser() =>', e);
      this.toast.error('Failed to update user access', e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  async resetPassword(username: string, poolId?: string): Promise<any> {
    let _id = this.loadingService.add('Reseting user password');
    try {
      const body: any = poolId ? { pool_id: poolId } : {};
      let res: any = await this.http.put(
        `${env.apiUrl}/rest/admin/users/${username}/reset_password`,
        body,
        this._get_headers(),
      ).toPromise();

      if (res?.err) {
        throw new Error(res.err);
      }

      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error resetPassword() =>', e);
      this.toast.error('Failed to reset user password', e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  /**
   * Admin password override -- direct AdminSetUserPassword on the backend.
   * No email goes out (bypasses the SES quota path). When `temporary` is
   * true the user is flagged FORCE_CHANGE_PASSWORD and the next sign-in
   * routes through NEW_PASSWORD_REQUIRED before reaching the SPA.
   *
   * `poolId` MUST be passed when the target row came from a per-customer
   * pool (Workstream A); without it the backend defaults to the legacy
   * pool and the override silently writes to the wrong Cognito identity.
   * The users grid stamps `pool_id` on every row (see get_users_across_pools).
   */
  async overridePassword(
    username: string,
    password: string,
    temporary: boolean,
    updateStoredForCustomers: string[] = [],
    poolId?: string,
  ): Promise<any> {
    const _id = this.loadingService.add(`Overriding password for ${username}`);
    try {
      const body: any = { password, temporary, update_stored_for_customers: updateStoredForCustomers };
      if (poolId) body.pool_id = poolId;
      const res: any = await this.http.put(
        `${env.apiUrl}/rest/admin/users/${username}/override_password`,
        body,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    } catch (e: any) {
      console.log('Error overridePassword() =>', e);
      this.toast.error(`Failed to override password for ${username}`, e.message);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  /**
   * Send a magic-link invite to an existing user. Backend uses the
   * user's pool's EmailConfiguration (SES identity, From) to send the
   * branded "Welcome — set your password" email. Redeem at
   * /accept-invite?token=... clears FORCE_CHANGE_PASSWORD via
   * AdminSetUserPassword(Permanent=true).
   *
   * poolId scopes to the right pool (same per-row routing as
   * overridePassword). Without it, falls back to legacy — which
   * isn't configured for branded SES, so the backend errors out.
   */
  async sendMagicLink(username: string, poolId?: string): Promise<any> {
    const _id = this.loadingService.add(`Sending magic-link invite to ${username}`);
    try {
      const body: any = {};
      if (poolId) body.pool_id = poolId;
      const res: any = await this.http.post(
        `${env.apiUrl}/rest/admin/users/${username}/send-magic-link`,
        body,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    } catch (e: any) {
      console.log('Error sendMagicLink() =>', e);
      this.toast.error(`Failed to send magic-link invite for ${username}`, e.message);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  /**
   * Mint a magic-link URL for an existing user WITHOUT sending the
   * email. Used by the Override Password modal's "Copy magic link"
   * button so the admin can paste it via Slack/SMS/etc.
   */
  async mintMagicLink(username: string, poolId?: string): Promise<any> {
    const _id = this.loadingService.add(`Minting magic-link for ${username}`);
    try {
      const body: any = {};
      if (poolId) body.pool_id = poolId;
      const res: any = await this.http.post(
        `${env.apiUrl}/rest/admin/users/${username}/mint-magic-link`,
        body,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    } catch (e: any) {
      console.log('Error mintMagicLink() =>', e);
      this.toast.error(`Failed to mint magic-link for ${username}`, e.message);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  /**
   * Admin "view as user" handoff -- mints a server-side sid carrying
   * the admin's tokens + an impersonate_as marker, returns the
   * destination URL the SPA opens in a new tab. See server-side
   * UsersController.impersonateUser for the safety + audit story.
   */
  async impersonate(
    username: string,
    kind: 'members' | 'scanner',
    scanner_id?: string,
    pool_id?: string,
  ): Promise<any> {
    const _id = this.loadingService.add(`Preparing impersonation tab`);
    try {
      const res: any = await this.http.post(
        `${env.apiUrl}/rest/admin/users/${username}/impersonate`,
        { kind, scanner_id, pool_id },
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    } catch (e: any) {
      console.log('Error impersonate() =>', e);
      this.toast.error('Could not start impersonation', e.message);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  async updateUserLiveAccess(username: string, live_access: boolean): Promise<any> {
    let _id = this.loadingService.add('Updating user\'s live data access');
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/live_access`,
        {
          user_id: username,
          live_access: live_access
        },
        this._get_headers(),
      ).toPromise();

      if (res?.err) {
        throw new Error(res.err);
      }

      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error updateUserLiveAccess() =>', e);
      this.toast.error('Failed to update user\'s live data access', e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  async syncUser(username: string): Promise<any> {
    let _id = this.loadingService.add('Syncing user');
    try {
      let res: any = await this.http.put(
        `${env.apiUrl}/rest/admin/users/${username}/sync`,
        {},
        this._get_headers(),
      ).toPromise();

      if (res?.err) {
        throw new Error(res.err);
      }

      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error syncUser() =>', e);
      this.toast.error('Failed to sync user', e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  // Force full Redis user-cache refresh from Cognito. Long-running (~30s
  // at current ~17k users). Caller should re-load the grid afterward.
  //
  // Progress: while the POST to /sync-all is inflight, poll the sibling
  // /sync-all/progress endpoint (Redis-backed, written per pagination
  // tick by cache_cognito_users) and update the loading-overlay
  // progress bar via LoadingService.setProgress. Denominator =
  // prev_total (HLEN F2:USER_CACHE) — drifts a bit when the user
  // population changes mid-sync, but clamped to [0,1] in LoadingService
  // so the bar can't visually overshoot.
  async syncAllUsers(): Promise<any> {
    const _id = this.loadingService.add('Refreshing Cognito user cache');
    this.loadingService.setProgress(_id, 0, 'starting…');
    const pollIntervalMs = 750;
    let poll: any = null;
    let stopped = false;
    const pollOnce = async () => {
      if (stopped) return;
      try {
        const p: any = await this.http.get(
          `${env.apiUrl}/rest/admin/users/sync-all/progress`,
          this._get_headers(),
        ).toPromise();
        if (stopped) return;
        const scanned = Number(p?.scanned_so_far ?? 0);
        const prev = Number(p?.prev_total ?? 0);
        // Determinate when we know a prior count; otherwise leave the
        // bar undefined (overlay falls back to spinner-only).
        const fraction = prev > 0 ? scanned / prev : undefined;
        const sub = prev > 0
          ? `${scanned.toLocaleString()} / ~${prev.toLocaleString()} users`
          : `${scanned.toLocaleString()} users so far`;
        this.loadingService.setProgress(_id, fraction ?? null, sub);
      } catch {
        // Polling is best-effort UX; a failed poll shouldn't tear down
        // the user-visible sync. Bar just stops updating.
      }
    };
    poll = setInterval(pollOnce, pollIntervalMs);
    // Fire one immediately so the first tick doesn't sit at 0 for ~750ms.
    pollOnce();
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/admin/users/sync-all`,
        {},
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      stopped = true;
      if (poll) clearInterval(poll);
      this.loadingService.remove(_id);
      // If the lock was held by another operator, backend returns
      // { ok: false, in_progress: true, started_at, by_email }. The
      // global SyncOverlayComponent picks this up over SSE and shows
      // the cross-tab banner; no caller-side error toast needed.
      return res;
    }
    catch (e: any) {
      console.log('Error syncAllUsers() =>', e);
      this.toast.error('Failed to refresh user cache', e.message);
      stopped = true;
      if (poll) clearInterval(poll);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  // Get all exchange agreements
  async getAllAgreements(query: any) {
    let _id = this.loadingService.add('Loading users');
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/all-agreements`,
        query,
        this._get_headers(),
      ).toPromise();

      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error syncUser() =>', e);
      this.toast.error('Failed to sync user', e.message);
      console.log('Error getAllAgreements() =>', e);
      this.toast.error('Failed to get exchange agreements', e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  // Get exchange agreement data for selected user
  async getSingleAgreement(user: any) {
    let _id = this.loadingService.add('Loading agreement details');
    try {
      const user_id = user.user_id ?? null;
      const customer = user.customer ?? null;
      let res: any = await this.http.get(
        `${env.apiUrl}/rest/user/data-agreements/admin/agreement/?user=${user_id}&customer=${customer}`,
        this._get_headers(),
      ).toPromise();

      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('getSingleAgreement() =>', e);
      this.toast.error('Failed to get agreement details', e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  // IT-F2-426 c/<parse-pdf> (Mike 2026-10-02). Parse a signed PDF
  // and return prefill candidates for the Add Agreement from PDF
  // modal. Stateless (no DB write). Silent on failure — the operator
  // can still fill fields manually.
  async parseSignedPdf(bytes_base64: string): Promise<any> {
    try {
      const res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/parse-pdf`,
        { bytes_base64 },
        this._get_headers(),
      ).toPromise();
      return res;
    } catch (e: any) {
      console.log('parseSignedPdf() =>', e);
      return { err: e?.message || String(e) };
    }
  }

  // IT-F2-426 c/<chunked-upload> (Mike 2026-10-05). Chunked PDF upload —
  // works around the shared nginx `client_max_body_size 1m` default on
  // web-backend-srvr-1. A 1.5 MB raw PDF (≈2 MB base64) in one POST
  // trips nginx and Vercel reflects it as a 502 CD8. Fanning across
  // ≤500 KB chunk POSTs slips under the cap and the backend reassembles.
  async beginPdfUpload(): Promise<{ upload_id: string; chunk_bytes: number; max_bytes: number; err?: string }> {
    try {
      const res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/pdf-upload/begin`,
        {},
        this._get_headers(),
      ).toPromise();
      return res;
    } catch (e: any) {
      return { upload_id: '', chunk_bytes: 0, max_bytes: 0, err: e?.message || String(e) };
    }
  }

  async appendPdfChunk(upload_id: string, index: number, bytes_base64: string): Promise<any> {
    try {
      const res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/pdf-upload/chunk`,
        { upload_id, index, bytes_base64 },
        this._get_headers(),
      ).toPromise();
      return res;
    } catch (e: any) {
      return { err: e?.message || String(e) };
    }
  }

  async finalizeParsePdfUpload(upload_id: string): Promise<any> {
    try {
      const res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/pdf-upload/parse`,
        { upload_id },
        this._get_headers(),
      ).toPromise();
      return res;
    } catch (e: any) {
      return { err: e?.message || String(e) };
    }
  }

  async finalizeCreateFromPdfUpload(args: {
    upload_id: string;
    user_id: string;
    customer: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    pro_access: boolean;
    filename: string;
    parsed?: any;
    form?: any;
  }): Promise<any> {
    const _id = this.loadingService.add('Creating agreement from PDF');
    try {
      const res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/pdf-upload/create`,
        args,
        this._get_headers(),
      ).toPromise();
      if (res?.err) {
        if (res?.existing_id) {
          this.toast.error('Agreement exists', `Already have an agreement for (${args.user_id}, ${args.customer}) — attach via Review instead.`);
          return res;
        }
        throw new Error(res.err);
      }
      this.toast.success('Agreement created', `${args.user_id} / ${args.customer}`);
      return res;
    } catch (e: any) {
      console.log('finalizeCreateFromPdfUpload() =>', e);
      this.toast.error('Create failed', e?.message || String(e));
      return { err: e?.message || String(e) };
    } finally {
      this.loadingService.remove(_id);
    }
  }

  async finalizeAttachSignedPdfUpload(args: {
    upload_id: string;
    user_id: string;
    customer: string;
    filename: string;
    content_type?: string;
  }): Promise<any> {
    const _id = this.loadingService.add('Uploading signed PDF');
    try {
      const res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/pdf-upload/attach`,
        args,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.toast.success('Signed PDF uploaded', `${args.filename}`);
      return res;
    } catch (e: any) {
      console.log('finalizeAttachSignedPdfUpload() =>', e);
      this.toast.error('Signed PDF upload failed', e?.message || String(e));
      return { err: e?.message || String(e) };
    } finally {
      this.loadingService.remove(_id);
    }
  }

  async abortPdfUpload(upload_id: string): Promise<void> {
    try {
      await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/pdf-upload/abort`,
        { upload_id },
        this._get_headers(),
      ).toPromise();
    } catch { /* silent */ }
  }

  // IT-F2-426 c/8255b2c8 (Mike 2026-10-02). Admin creates an
  // Exchange_Agreements row from a signed-offline PDF (trust-approve).
  async createAgreementFromPdf(args: {
    user_id: string;
    customer: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    pro_access: boolean;
    filename: string;
    bytes_base64: string;
    // IT-F2-426 c/<parse-pdf-version> (Mike 2026-10-02): the parsed
    // metadata from parseSignedPdf — ships back to the create endpoint
    // so the stored row carries the version/date provenance.
    parsed?: any;
    // IT-F2-426 c/e12385a8 (Mike 2026-10-02): full standard-form
    // payload (contact, address, employment, 11 questions) matching
    // the regular user flow's agreement shape. Merged over parsed
    // defaults by the backend.
    form?: any;
  }): Promise<any> {
    const _id = this.loadingService.add('Creating agreement from PDF');
    try {
      const res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/create-from-pdf`,
        args,
        this._get_headers(),
      ).toPromise();
      if (res?.err) {
        // Collision case — surface existing-id so SPA can route the
        // operator to the Review workflow instead.
        if (res?.existing_id) {
          this.toast.error('Agreement exists', `Already have an agreement for (${args.user_id}, ${args.customer}) — attach via Review instead.`);
          return res;
        }
        throw new Error(res.err);
      }
      this.toast.success('Agreement created', `${args.user_id} / ${args.customer}`);
      return res;
    } catch (e: any) {
      console.log('createAgreementFromPdf() =>', e);
      this.toast.error('Create failed', e?.message || String(e));
      return { err: e?.message || String(e) };
    } finally {
      this.loadingService.remove(_id);
    }
  }

  // IT-F2-426 c/f4081b4b (Mike 2026-10-02). Signed-PDF attachment on
  // Exchange_Agreements. Upload sends base64 JSON (mirrors customer-
  // assets pattern); download is an authenticated blob GET; remove is
  // a DELETE with query params.
  async attachSignedPdf(args: {
    user_id: string;
    customer: string;
    filename: string;
    bytes_base64: string;
  }): Promise<any> {
    const _id = this.loadingService.add('Uploading signed PDF');
    try {
      const res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/signed-pdf`,
        { ...args, content_type: 'application/pdf' },
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.toast.success('Signed PDF uploaded', `${args.filename}`);
      return res;
    } catch (e: any) {
      console.log('attachSignedPdf() =>', e);
      this.toast.error('Signed PDF upload failed', e?.message || String(e));
      return { err: e?.message || String(e) };
    } finally {
      this.loadingService.remove(_id);
    }
  }

  signedPdfUrl(user_id: string, customer: string, download = false): string {
    const q = new URLSearchParams({ user_id, customer });
    if (download) q.set('download', '1');
    return `${env.apiUrl}/rest/user/data-agreements/admin/signed-pdf?${q.toString()}`;
  }

  async getSignedPdfMeta(user_id: string, customer: string): Promise<any> {
    try {
      const res: any = await this.http.get(
        `${env.apiUrl}/rest/user/data-agreements/admin/signed-pdf?user_id=${encodeURIComponent(user_id)}&customer=${encodeURIComponent(customer)}&meta=1`,
        this._get_headers(),
      ).toPromise();
      return res;
    } catch (e: any) {
      return { err: e?.message || String(e) };
    }
  }

  async removeSignedPdf(user_id: string, customer: string): Promise<any> {
    const _id = this.loadingService.add('Removing signed PDF');
    try {
      const res: any = await this.http.delete(
        `${env.apiUrl}/rest/user/data-agreements/admin/signed-pdf?user_id=${encodeURIComponent(user_id)}&customer=${encodeURIComponent(customer)}`,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.toast.success('Signed PDF removed', `${user_id} / ${customer}`);
      return res;
    } catch (e: any) {
      console.log('removeSignedPdf() =>', e);
      this.toast.error('Signed PDF remove failed', e?.message || String(e));
      return { err: e?.message || String(e) };
    } finally {
      this.loadingService.remove(_id);
    }
  }

  // IT-F2-223 Mike 2026-09-01: toggle Pro/Non-Pro on a single agreement.
  // Body: { user_id, customer, pro_access: boolean }. Backend updates
  // main or legacy (whichever has the row) and invalidates the report cache.
  async setAgreementProAccess(user_id: string, customer: string, pro_access: boolean) {
    try {
      const res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/agreement/pro-access`,
        { user_id, customer, pro_access },
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      return res;
    }
    catch (e: any) {
      console.log('setAgreementProAccess() =>', e);
      this.toast.error('Failed to update Pro/Non-Pro', e.message);
      return { err: e.message };
    }
  }

  // IT-F2-223 — NYSE Exhibit B compliance report. Body: {customers?, date_from?, date_to?}
  // Returns {rows} where each row has the 36 Exhibit B columns.
  async getExhibitBReport(query: any) {
    let _id = this.loadingService.add('Compiling compliance report');
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/exhibit-b`,
        query,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('getExhibitBReport() =>', e);
      this.toast.error('Failed to compile compliance report', e.message);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  // IT-F2-426 (Mike 2026-09-24 c/b89b32fe + c/49315c17): per-database
  // per-month unique-user counts. Same {customers?} query shape as
  // getExhibitBReport. Returns {rows: [{SOURCE, MONTH, PRO, NON_PRO,
  // TOTAL}]}.
  async getAuthLoginsSourceMonthly(query: any) {
    let _id = this.loadingService.add('Compiling source coverage');
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/authlogins-source-monthly`,
        query,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('getAuthLoginsSourceMonthly() =>', e);
      this.toast.error('Failed to compile source coverage', e.message);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  // IT-F2-426 checklist c0019445: event-count companion to
  // getAuthLoginsSourceMonthly. Same query shape, same {rows:[...]}
  // return shape. Numbers are raw event counts, not unique users.
  async getAuthLoginsSourceMonthlyEvents(query: any) {
    let _id = this.loadingService.add('Compiling event counts');
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/authlogins-source-monthly-events`,
        query,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('getAuthLoginsSourceMonthlyEvents() =>', e);
      this.toast.error('Failed to compile event counts', e.message);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  // IT-F2-426 checklist 18d4b626 + d0e59c93: per-user AuthLogins history
  // for the drill popup "View Logins" button. Body: { email, customer?,
  // limit?, date_from?, date_to? } — date range lets the modal inherit
  // the drill's month scope so the operator sees logins for just that
  // month, not the user's entire history.
  async getUserLoginHistory(query: { email: string; customer?: string; limit?: number; date_from?: string; date_to?: string }) {
    let _id = this.loadingService.add(`Loading login history for ${query.email}`);
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/user-login-history`,
        query,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('getUserLoginHistory() =>', e);
      this.toast.error('Failed to load login history', e.message);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  // IT-F2-426 checklist 518a835e: drill-down for a Counts by Month
  // DELAYED cell. Body: { month:'YYYY-MM', customers?, date_from?, date_to? }.
  async getDelayedMonthlyDetail(query: { month: string; customers?: string[]; date_from?: string; date_to?: string }) {
    let _id = this.loadingService.add(`Loading detail for Delayed ${query.month}`);
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/delayed-monthly-detail`,
        query,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('getDelayedMonthlyDetail() =>', e);
      this.toast.error('Failed to load delayed detail', e.message);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  // IT-F2-426 checklist b86d9456: per-month Delayed-user counts (users
  // seen in a login source with no Exchange_Agreement) scoped to the
  // Counts by Month view's date window. Body: { customers?, date_from?,
  // date_to?, ym_from?, ym_to? }. Returns { rows: [{MONTH, DELAYED}] }.
  async getDelayedMonthlyCounts(query: any) {
    let _id = this.loadingService.add('Compiling delayed monthly counts');
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/delayed-monthly-counts`,
        query,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('getDelayedMonthlyCounts() =>', e);
      this.toast.error('Failed to compile delayed monthly counts', e.message);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  // IT-F2-426 checklist c0019445: drill-down for a single (source, month)
  // cell — returns contributing users + totals so the operator sees how
  // the aggregate number was computed.
  async getAuthLoginsSourceMonthlyDetail(query: { source: string; month: string; customers?: string[] }) {
    let _id = this.loadingService.add(`Loading detail for ${query.source} / ${query.month}`);
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/authlogins-source-monthly-detail`,
        query,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('getAuthLoginsSourceMonthlyDetail() =>', e);
      this.toast.error('Failed to load cell detail', e.message);
      this.loadingService.remove(_id);
      return { err: e.message };
    }
  }

  // IT-F2-367 — firm compliance metadata for NYSE §9.2 Pro entitlement
  // report. Backed by Customer.f2 doc (VAN, legal name, installation
  // address, data center location, default TYPE_OF_PRODUCT).
  async getComplianceCompanyInfo() {
    try {
      let res: any = await this.http.get(
        `${env.apiUrl}/rest/user/data-agreements/admin/company-info`,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      return res;
    }
    catch (e: any) {
      console.log('getComplianceCompanyInfo() =>', e);
      return { err: e.message };
    }
  }

  async patchComplianceCompanyInfo(body: any) {
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/company-info`,
        body,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      return res;
    }
    catch (e: any) {
      console.log('patchComplianceCompanyInfo() =>', e);
      return { err: e.message };
    }
  }

  // IT-F2-367 — F2 employee agreement CRUD for the Employee view mode.
  // Backed by Exchange_Agreements on (user_id.lower(), customer) key.
  async listEmployeeAgreements(customer: string = 'f2') {
    try {
      let res: any = await this.http.get(
        `${env.apiUrl}/rest/user/data-agreements/admin/employees?customer=${encodeURIComponent(customer)}`,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      return res;
    }
    catch (e: any) {
      console.log('listEmployeeAgreements() =>', e);
      return { err: e.message };
    }
  }

  async upsertEmployeeAgreement(body: any) {
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/employee`,
        body,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      return res;
    }
    catch (e: any) {
      console.log('upsertEmployeeAgreement() =>', e);
      return { err: e.message };
    }
  }

  async deleteEmployeeAgreement(user_id: string, customer: string = 'f2') {
    try {
      let res: any = await this.http.delete(
        `${env.apiUrl}/rest/user/data-agreements/admin/employee/${encodeURIComponent(user_id)}?customer=${encodeURIComponent(customer)}`,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      return res;
    }
    catch (e: any) {
      console.log('deleteEmployeeAgreement() =>', e);
      return { err: e.message };
    }
  }

  // IT-F2-391 2026-09-15 (Mike c/91efd8cd): admin approves or declines a
  // pending Exchange_Agreements row from the Pending Review tab in
  // compliance-report. Body: {user_id, customer, decision, reason?,
  // pro_override?}.  decision must be 'approved' or 'declined'. Backend
  // writes a ReviewAudit row and (on approve) fires the Cognito
  // live_data_access flip.
  //
  // pro_override (IT-F2-391 Mike c/6d907651): optional admin override of
  // the question-derived pro_access value. `true` = "Approve as Pro",
  // `false` = "Approve as Non-Pro", `undefined` = leave pro_access as-is
  // (legacy single-Approve callers).
  // IT-F2-400 2026-09-16 (Mike c/eb18c8dd): lazy-load resubmission history
  // for a specific agreement. Called by the admin Review panel modal
  // when the user clicks Older/Newer. Split out from the main exhibit-b
  // report to keep the report response lean (main report was carrying
  // ~40 KB per row per resubmitting user regardless of whether the
  // modal was opened).
  async getAgreementHistory(user_id: string, customer: string) {
    try {
      const params = new URLSearchParams({ user_id, customer }).toString();
      const res: any = await this.http.get(
        `${env.apiUrl}/rest/user/data-agreements/admin/agreement/history?${params}`,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      return res as { total_submission_count: number; previous_submissions: any[] };
    }
    catch (e: any) {
      console.log('getAgreementHistory() =>', e);
      this.toast.error('History fetch failed', e.message);
      return { err: e.message, total_submission_count: 0, previous_submissions: [] };
    }
  }

  async reviewUserDataAgreement(
    user_id: string,
    customer: string,
    decision: 'approved' | 'declined',
    reason?: string,
    pro_override?: boolean,
  ) {
    try {
      const body: any = { user_id, customer, decision };
      if (reason) body.reason = reason;
      if (typeof pro_override === 'boolean') body.pro_override = pro_override;
      const res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/review`,
        body,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      return res;
    }
    catch (e: any) {
      console.log('reviewUserDataAgreement() =>', e);
      this.toast.error('Review failed', e.message);
      return { err: e.message };
    }
  }

  // IT-F2-391 2026-09-15 (Mike c/ff3ca265 — Re-eval button): admin
  // forces a pro_access + live_data_access re-derivation on an
  // existing Exchange_Agreements row from its currently-stored question
  // answers — no edit. Backend response includes {before,after} so the
  // caller can toast the diff (or "no change" if the derivation matches
  // the currently-stored classification).
  async reevaluateUserDataAgreement(user_id: string, customer_slug: string) {
    try {
      const body: any = { user_id, customer: customer_slug };
      const res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/admin/reeval`,
        body,
        this._get_headers(),
      ).toPromise();
      if (res?.err) throw new Error(res.err);
      return res;
    }
    catch (e: any) {
      console.log('reevaluateUserDataAgreement() =>', e);
      this.toast.error('Re-eval failed', e.message);
      return { err: e.message };
    }
  }

  /**
   * Single-row cached read used by the live-grid SSE consumer. Backed
   * by GET /admin/users/:username/cached?pool_id=... — reads Redis,
   * no Cognito round-trip. Returns the normalized single row OR null
   * when the caller isn't entitled / the row was just deleted.
   *
   * No loading-service overlay: this gets called once per SSE event
   * and an overlay flashing would be jarring for the operator.
   */
  async getCachedUser(username: string, poolId?: string): Promise<any> {
    try {
      const url = poolId
        ? `${env.apiUrl}/rest/admin/users/${encodeURIComponent(username)}/cached?pool_id=${encodeURIComponent(poolId)}`
        : `${env.apiUrl}/rest/admin/users/${encodeURIComponent(username)}/cached`;
      const res: any = await this.http.get(url, this._get_headers()).toPromise();
      if (res?.err) return null;
      return res || null;
    } catch (e: any) {
      console.warn('getCachedUser() =>', e?.message);
      return null;
    }
  }

  /**
   * Mint a 60s HMAC bearer for the live-users EventSource. Cookie-
   * authed (rides session cookies via withCredentials). Returns the
   * bare string token; caller appends to /users/stream?token=...
   */
  async mintUsersStreamToken(): Promise<string | null> {
    try {
      const res: any = await this.http.post(
        `${env.apiUrl}/rest/admin/users-live/stream-token`,
        {},
        this._get_headers(),
      ).toPromise();
      return res?.token || null;
    } catch (e: any) {
      console.warn('mintUsersStreamToken() =>', e?.message);
      return null;
    }
  }

  /**
   * URL for the live-users EventSource handshake. Built from env here
   * so the component doesn't need to import the env file. Token rides
   * the query string — see SSE_TOKEN block in UsersController on why
   * the bearer can't be a header.
   */
  getUsersStreamUrl(token: string): string {
    return `${env.apiUrl}/rest/admin/users-live/stream?token=${encodeURIComponent(token)}`;
  }
}